import React, { useState } from "react";

interface GridRow {
  SchoolCode:          string;
  FamilyCode:          string;
  Login:               string;
  ReportNo:            string;
  StudyYear:           string;
  Score:               string;
  FName:               string;
  LName:               string;
  TestCompletionDate:  string;
  Email:               string;
  [key: string]: string;
}

interface SubmitResponse {
  totalRows:   number;
  emailsSent:  number;
  gridRows:    GridRow[];
  columns:     string[];
  error?:      string;
}

const API_BASE = process.env.REACT_APP_DOCUMENT_LINK;
const SendEmail: React.FC = () => {
  const [schoolCode, setSchoolCode] = useState("");
  const [familyCode, setFamilyCode] = useState("");
  const [login,      setLogin]      = useState("");
  const [reportNo,   setReportNo]   = useState("");   // in ASPX but unused in VB

  const [loading,    setLoading]    = useState(false);
  const [result,     setResult]     = useState<SubmitResponse | null>(null);
  const [error,      setError]      = useState("");


  const handleSubmit = async () => {
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const res = await fetch(`${API_BASE}/api/send-email/submit/`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          schoolCode: schoolCode.trim(),
          familyCode: familyCode.trim(),
          login:      login.trim(),
          reportNo:   reportNo.trim(),
        }),
      });
      const data: SubmitResponse = await res.json();
      if (data.error) {
        setError(data.error);
      } else {
        setResult(data);
      }
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  };

  const labelStyle: React.CSSProperties = {
    fontFamily: "Arial, sans-serif",
    fontSize:   "10pt",
    marginRight: 4,
  };
  const inputStyle: React.CSSProperties = {
    fontFamily:  "Trebuchet MS, sans-serif",
    fontSize:    "10pt",
    border:      "1px solid #006600",
    paddingLeft: 5,
    marginRight: 12,
  };
  const btnStyle: React.CSSProperties = {
    backgroundColor: "#119ECC",
    fontFamily:      "Trebuchet MS, sans-serif",
    fontSize:        "8pt",
    color:           "white",
    border:          "none",
    height:          24,
    minWidth:        70,
    cursor:          "pointer",
    marginTop:       8,
  };
  const thStyle: React.CSSProperties = {
    fontFamily:      "Trebuchet MS, sans-serif",
    fontSize:        "8pt",
    backgroundColor: "#2f6089",
    color:           "white",
    fontWeight:      "normal",
    padding:         "3px 6px",
    border:          "1px solid #4a7aab",
    whiteSpace:      "nowrap",
  };
  const tdStyle: React.CSSProperties = {
    fontFamily: "Trebuchet MS, sans-serif",
    fontSize:   "8pt",
    padding:    "2px 6px",
    border:     "1px solid #d0d0d0",
    verticalAlign: "top",
  };

  return (
    <div style={{ margin: 0, padding: 8, fontFamily: "Arial, sans-serif" }}>
      {/* ── form1 equivalent ── */}
      <div>

        {/* ── Label1 + txtSchoolCode ── */}
        <label style={labelStyle}>School Code :</label>
        <input
          id="txtSchoolCode"
          type="text"
          value={schoolCode}
          onChange={e => setSchoolCode(e.target.value)}
          style={inputStyle}
        />

        {/* ── Label2 + txtFamilyCode ── */}
        <label style={labelStyle}>Family Code :</label>
        <input
          id="txtFamilyCode"
          type="text"
          value={familyCode}
          onChange={e => setFamilyCode(e.target.value)}
          style={inputStyle}
        />

        {/* ── Label3 + txtLogin ── */}
        <label style={labelStyle}>Login :</label>
        <input
          id="txtLogin"
          type="text"
          value={login}
          onChange={e => setLogin(e.target.value)}
          style={inputStyle}
        />

        {/* ── Label4 + txtReportNo (present in ASPX, unused in VB) ── */}
        <label style={labelStyle}>Report No :</label>
        <input
          id="txtReportNo"
          type="text"
          value={reportNo}
          onChange={e => setReportNo(e.target.value)}
          style={inputStyle}
        />

        {/* ── btnSubmit ── */}
        <button
          id="btnSubmit"
          onClick={handleSubmit}
          disabled={loading}
          style={btnStyle}
        >
          {loading ? "Submitting..." : "Submit"}
        </button>

        {/* ── Error display ── */}
        {error && (
          <div style={{ color: "red", marginTop: 8, fontFamily: "Arial", fontSize: "9pt" }}>
            <b>Error:</b>
            <pre style={{ whiteSpace: "pre-wrap", fontSize: "8pt" }}>{error}</pre>
          </div>
        )}

        {/* ── Result summary ── */}
        {result && (
          <div style={{ marginTop: 6, fontFamily: "Arial", fontSize: "9pt", color: "#2f6089" }}>
            Total rows returned: <b>{result.totalRows}</b> &nbsp;|&nbsp;
            Emails queued (rows processed): <b>{result.emailsSent}</b>
            &nbsp;
            <span style={{ color: "#888", fontSize: "8pt" }}>
              (Note: VB loop starts at row index 1, skipping row 0 — replicated exactly)
            </span>
          </div>
        )}

        {/* ── gvEmailList (GridView equivalent) ── */}
        {result && result.gridRows.length > 0 && (
          <div style={{ marginTop: 10, overflowX: "auto" }}>
            <table
              id="gvEmailList"
              cellPadding={0}
              cellSpacing={0}
              border={0}
              style={{
                borderCollapse: "collapse",
                width: "100%",
                fontFamily: "Trebuchet MS, sans-serif",
                fontSize: "8pt",
              }}
            >
              <thead>
                <tr>
                  {result.columns.map(col => (
                    <th key={col} style={thStyle}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.gridRows.map((row, idx) => (
                  <tr
                    key={idx}
                    style={{
                      backgroundColor: idx % 2 === 0 ? "white" : "#EFF3FB",
                    }}
                  >
                    {result.columns.map(col => (
                      <td key={col} style={tdStyle}>
                        {row[col] || "\u00a0"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {result && result.gridRows.length === 0 && (
          <div style={{ marginTop: 10, fontFamily: "Trebuchet MS", fontSize: "9pt", color: "#888" }}>
            No records returned.
          </div>
        )}

      </div>
    </div>
  );
};

export default SendEmail;